/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import "./dashboards.css";
import {
  GET_DASHBOARDS,
  CREATE_DASHBOARD,
  UPDATE_DASHBOARD,
  DELETE_DASHBOARD,
  SET_DEFAULT_DASHBOARD,
} from "./dashboardStore";
import { UseUserDetails, UseIsAdmin } from "../../../hooks/auth";
import { UseCompanyWorkspaces } from "../../../hooks/company";
import useDashboardMetrics from "./useDashboardMetrics";
import { TEMPLATES, COMPONENT_TEMPLATES, widgetsFromTemplate } from "./widgetTypes";
import { WIDGETS, makeCustomId } from "../../Homepage/customize/widgetRegistry";
import DashboardsHome from "./DashboardsHome";
import DashboardBuilder from "./DashboardBuilder";
import NewDashboardWizard from "./NewDashboardWizard";

/**
 * Container for the Dashboards tab. Owns the list of dashboards, the shared
 * metric data, and switching between the library ("home") and the builder.
 */
export default function DashboardsPanel({ brandPrimary }) {
  const { _id: userId } = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const metricsApi = useDashboardMetrics();
  const workspaces = UseCompanyWorkspaces(true) || [];
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis) || {};

  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // the dashboard being edited
  const [busy, setBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const loadList = useCallback(async () => {
    /* After a server restart the panel can mount before the JWT is on the
       wire — the first GET /api/dashboard 401'd and we cached [] forever. */
    if (!localStorage.getItem("token")) return;
    setLoading(true);
    try {
      const res = await GET_DASHBOARDS();
      setDashboards(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setDashboards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList, userId]);

  useEffect(() => {
    const onFocus = () => loadList();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadList]);

  // The open dashboard lives in the URL (?dashboard=<id>) so a refresh, a shared
  // link, or the browser back button all land where the user was. Opening pushes
  // the param; closing removes it. Also serves Settings → TV View → "Edit
  // dashboard", which deep-links the same way. `active` stays a stable snapshot
  // (not re-derived from the list) so a focus-refetch can't swap stale widgets
  // under a builder with an autosave pending.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantId = searchParams.get("dashboard");

  const setDashboardParam = useCallback((id, replace) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("dashboard", id);
      else next.delete("dashboard");
      return next;
    }, { replace });
  }, [setSearchParams]);

  const openDashboard = useCallback((d) => {
    setActive(d);
    setDashboardParam(d._id, false);
  }, [setDashboardParam]);

  const closeDashboard = useCallback(() => {
    setActive(null);
    setDashboardParam(null, false);
  }, [setDashboardParam]);

  useEffect(() => {
    if (!wantId) {
      if (active) setActive(null); // browser back / param removed
      return;
    }
    if (active && active._id === wantId) return;
    if (loading) return;
    const target = dashboards.find((d) => d._id === wantId);
    if (target) setActive(target);
    else setDashboardParam(null, true); // deleted or not accessible
  }, [wantId, active, loading, dashboards, setDashboardParam]);

  // The wizard hands over a complete definition — create the record once,
  // fully assembled, and land straight in the builder.
  /* Wizard-chosen components → dashboard widgets, stacked below the metrics.
     Sizes mirror the builder's add drawer (24-col registry → 12-col grid). */
  const componentWidgets = (ids, startY) => {
    let y = startY || 0;
    return (ids || []).map((id) => {
      const def = WIDGETS.find((w) => w.id === id);
      const w = Math.max(4, Math.min(12, Math.round((def?.defaultW || 12) / 2)));
      const h = Math.max(5, Math.round((((def?.defaultH || 10) * 8) + (((def?.defaultH || 10) - 1) * 32)) / 56));
      const widget = { kind: "component", comp: id, title: def?.title || "", x: 0, y, w, h, options: {} };
      y += h;
      return widget;
    });
  };

  /* A component (role) template → fully configured widgets, using the REAL
     registry components (no lookalikes). Resolves the template's workspace
     NAMES to ids, scopes every card to them with the team average, and hands
     each its saved options (scene, payout curve, title). A `custom: true` entry
     becomes a fresh Build-your-own card; its metric is set to a revenue metric
     of the first scoped workspace when one is known. */
  const componentTemplateWidgets = (tpl) => {
    /* Match on a normalized name (lowercase, letters+digits only) so a hyphen,
       en-dash or spacing difference — "In-House Sales" vs "In House Sales" —
       still resolves. */
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const wsIds = (tpl.workspaceNames || [])
      .map((n) => workspaces.find((w) => norm(w.name) === norm(n)))
      .filter(Boolean)
      .map((w) => String(w._id));
    /* The revenue metric that drives the bonus/payout-curve card. Two realities
       make this more than "first $ metric":
        1. A workspace has several dollar metrics with wildly different targets
           (AVG Ticket, Sold Revenue, Estimate Won Value…), so pick by PRIORITY —
           the booked-revenue KPI the milestone/earnings cards also use ("Estimate
           Won Value") first, since it carries the real target the payout curve
           reads against; then any other revenue metric; then any dollar one.
        2. The two sales teams track revenue under DIFFERENT names — "Estimate Won
           Value" lives only in In-House Sales, "Sold Revenue" only in Sales — so
           the search runs ACROSS every scoped workspace, priority first. The pick
           carries its workspace back: a custom card's metric id must belong to its
           HOME workspace (that is what builds ctx.kpis), so the card is homed on
           whichever team actually has the chosen metric. Kept in sync with the
           render-time self-heal in ComponentCard. */
    const dollarsIn = (wsId) => (companyKpis[wsId] || [])
      .filter((k) => k && !k.isDraft && !k.disabled && k.kind === "$");
    const dollarsByWs = wsIds.map((id) => ({ id, list: dollarsIn(id) }));
    const matchAcross = (re) => {
      for (let i = 0; i < dollarsByWs.length; i += 1) {
        const hit = dollarsByWs[i].list.find((k) => re.test(k.title || k.name || ""));
        if (hit) return { wsId: dollarsByWs[i].id, metricId: hit._id };
      }
      return null;
    };
    const pickRevenue = () => matchAcross(/estimate won|won value/i)
      || matchAcross(/revenue/i)
      || matchAcross(/\bsales\b|booked|contract/i)
      || (dollarsByWs[0] && dollarsByWs[0].list[0]
        ? { wsId: dollarsByWs[0].id, metricId: dollarsByWs[0].list[0]._id }
        : null);
    return (tpl.components || []).map((c) => {
      const comp = c.custom ? makeCustomId() : c.comp;
      const def = c.custom ? null : WIDGETS.find((w) => w.id === c.comp);
      /* `workspaceId` (singular) is the card's HOME workspace — what ComponentCard
         reads to build ctx.kpis (via useWorkspaceData) and to fetch the pay
         program. Without it the card falls back to workspaces[0] (the wrong team),
         and a custom card's baked metric id — which belongs to a scoped workspace
         — is not in those kpis, so it renders "not available yet". `workspaceIds`
         (plural) is the SCOPE the card averages across. Both are set. */
      const options = {
        ...(c.options || {}),
        workspaceId: wsIds[0] || undefined,
        workspaceIds: wsIds,
        avgMode: "avg",
      };
      if (c.custom && (!options.metrics || !options.metrics.length)) {
        const pick = pickRevenue();
        if (pick) {
          /* Home the card on the team that actually has the metric, so ctx.kpis
             contains it; scope still spans every team for the average. */
          options.workspaceId = pick.wsId;
          options.metrics = [{ metricId: pick.metricId, graph: "number" }];
        }
      }
      return {
        kind: "component",
        comp,
        title: (c.options && c.options.title) || def?.title || "",
        x: c.x, y: c.y, w: c.w, h: c.h,
        options,
      };
    });
  };

  const createFromWizard = async ({ name, templateId, componentTemplateId, metricIds, componentIds }) => {
    if (busy) return;
    setBusy(true);
    try {
      /* Component (role) template — a ready board of real components. */
      const roleTpl = componentTemplateId
        ? COMPONENT_TEMPLATES.find((t) => t.id === componentTemplateId)
        : null;
      if (roleTpl) {
        const res = await CREATE_DASHBOARD({
          ownerId: userId,
          name: name || roleTpl.name,
          template: roleTpl.id,
          widgets: componentTemplateWidgets(roleTpl),
        });
        if (res?.data) {
          setDashboards((prev) => [res.data, ...prev]);
          openDashboard(res.data);
          setWizardOpen(false);
        }
        return;
      }
      const tpl = TEMPLATES.find((t) => t.id === templateId) || null;
      const metricWidgets = widgetsFromTemplate(tpl, metricIds);
      /* Components chosen in Step 2 append below the metric widgets, each as a
         `kind: component` widget the builder already knows how to render. */
      const startY = metricWidgets.reduce((m, w) => Math.max(m, w.y + w.h), 0);
      const compWidgets = componentWidgets(componentIds, startY);
      const res = await CREATE_DASHBOARD({
        ownerId: userId,
        name,
        template: tpl ? tpl.id : "",
        widgets: [...metricWidgets, ...compWidgets],
      });
      if (res?.data) {
        setDashboards((prev) => [res.data, ...prev]);
        openDashboard(res.data);
        setWizardOpen(false);
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(
        err?.response?.data?.error?.message
          || err?.message
          || "Could not save dashboard to the server. Is /api/dashboard deployed?",
      );
    } finally {
      setBusy(false);
    }
  };

  const duplicateDashboard = async (d) => {
    const res = await CREATE_DASHBOARD({
      ownerId: userId,
      name: `${d.name} copy`,
      widgets: (d.widgets || []).map((w) => ({
        kind: w.kind,
        metric: w.metric,
        comp: w.comp,
        title: w.title,
        chartType: w.chartType,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        options: w.options,
      })),
    });
    if (res?.data) setDashboards((prev) => [res.data, ...prev]);
  };

  const renameDashboard = async (id, name) => {
    setDashboards((prev) => prev.map((d) => (d._id === id ? { ...d, name } : d)));
    try {
      await UPDATE_DASHBOARD(id, { name });
    } catch (err) {
      /* optimistic; keep name */
    }
  };

  const removeDashboard = async (id) => {
    setDashboards((prev) => prev.filter((d) => d._id !== id));
    try {
      await DELETE_DASHBOARD(id);
    } catch (err) {
      loadList();
    }
  };

  // Flip a board on/off the TV rotation from the card menu — optimistic, quiet.
  const toggleTv = async (d) => {
    const next = !d.showOnTv;
    setDashboards((prev) => prev.map((x) => (x._id === d._id ? { ...x, showOnTv: next } : x)));
    try {
      await UPDATE_DASHBOARD(d._id, { showOnTv: next });
    } catch (err) {
      loadList();
    }
  };

  const setDefault = async (id) => {
    setDashboards((prev) => prev.map((d) => ({ ...d, isDefault: d._id === id })));
    try {
      await SET_DEFAULT_DASHBOARD(id);
    } catch (err) {
      loadList();
    }
  };

  // Keep BOTH the list and the open dashboard in sync with every save — if
  // `active` stays stale, a builder remount re-hydrates from old (even empty)
  // widgets and the next autosave overwrites the real ones.
  const onSaved = useCallback((updated) => {
    setDashboards((prev) => prev.map((d) => (d._id === updated._id ? { ...d, ...updated } : d)));
    setActive((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated } : prev));
  }, []);

  const style = brandPrimary
    ? { "--db-brand": brandPrimary, "--db-brand-soft": `${brandPrimary}1a` }
    : undefined;

  return (
    <div className="db-root" style={style}>
      {active ? (
        <DashboardBuilder
          dashboard={active}
          metricsApi={metricsApi}
          onBack={closeDashboard}
          onSaved={onSaved}
        />
      ) : (
        <DashboardsHome
          dashboards={dashboards}
          metricsApi={metricsApi}
          loading={loading}
          currentUserId={userId}
          isAdmin={isAdmin}
          onOpen={openDashboard}
          onCreate={() => setWizardOpen(true)}
          onDuplicate={duplicateDashboard}
          onRename={renameDashboard}
          onDelete={removeDashboard}
          onSetDefault={setDefault}
          onToggleTv={toggleTv}
        />
      )}

      <NewDashboardWizard
        open={wizardOpen}
        metricsApi={metricsApi}
        onCreate={createFromWizard}
        onClose={() => setWizardOpen(false)}
        busy={busy}
      />
    </div>
  );
}

DashboardsPanel.propTypes = {
  brandPrimary: PropTypes.string,
};
